import FormattedMessage from 'I18n/components/FormattedMessage';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { ChickletHoverActions } from '../extensions/constants/BehaviorTypes';
import { useBehavior } from '../extensions/hooks/useBehavior';
import ChickletHoverButton from './ChickletHoverButton';
import { PRIMARY_STATE, SECONDARY_STATE } from '../types/ChickletHoverStates';
import { useAssociatedObjectsCardContentContext } from '../context/AssociatedObjectsCardContext';
import { getIsCompanyPrimaryAssociationLegacy } from '../helpers/objectUtilFunction';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { UPDATE_ASSOCIATION_MODAL_STATES } from 'crm-message-bus/types/UpdateAssociationModalStates';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ChickletAssociationButtons = ({
  associationDefinitions,
  associatedObjectsIds,
  canView,
  fromObjectTypeId,
  objectId,
  objectTypeId,
  onPreview,
  primaryAssociationObjectId,
  fromObjectId
}) => {
  const {
    hasScope,
    isUngated,
    userPermissions,
    readOnly,
    associationTypesData: {
      unlabeledAssociationType
    }
  } = useAssociatedObjectsCardContentContext();
  const canEditAssociation = userPermissions.currentUserCanEditAssociations && !readOnly;
  const sendOpenUpdateAssociationModal = useSendCrmMessageTopic('OPEN_UPDATE_ASSOCIATION_MODAL');
  const isPrimary = objectId === primaryAssociationObjectId;
  const handleOpenPrimaryModal = useCallback(() => {
    let modalState;
    if (isPrimary) {
      modalState = UPDATE_ASSOCIATION_MODAL_STATES.PRIMARY_MODAL_PRIMARY_STATE;
    } else if (primaryAssociationObjectId) {
      modalState = UPDATE_ASSOCIATION_MODAL_STATES.PRIMARY_MODAL_SECONDARY_PRIMARY_EXISTS_STATE;
    } else {
      modalState = UPDATE_ASSOCIATION_MODAL_STATES.PRIMARY_MODAL_SECONDARY_ASSOCIATION_PRIMARY_DOESNT_EXIST_STATE;
    }
    sendOpenUpdateAssociationModal({
      currentState: modalState,
      fromObjectId,
      fromObjectTypeId,
      toObjectId: objectId,
      toObjectTypeId: objectTypeId
    });
  }, [primaryAssociationObjectId, sendOpenUpdateAssociationModal, fromObjectId, fromObjectTypeId, objectId, objectTypeId, isPrimary]);
  const handleOpenEditAssociationModal = useCallback(() => {
    sendOpenUpdateAssociationModal({
      currentState: UPDATE_ASSOCIATION_MODAL_STATES.EDIT_ASSOCIATION_MODAL_STATE,
      fromObjectId,
      fromObjectTypeId,
      toObjectId: objectId,
      toObjectTypeId: objectTypeId
    });
  }, [sendOpenUpdateAssociationModal, fromObjectId, fromObjectTypeId, objectId, objectTypeId]);
  const totalAssociations = associatedObjectsIds.length;
  const isCompanyPrimaryAssociation = getIsCompanyPrimaryAssociationLegacy(fromObjectTypeId, objectTypeId, associationDefinitions);
  const handleOpenRemoveAssociationModal = useCallback(() => {
    let modalState;
    if (isPrimary) {
      if (totalAssociations === 1) {
        modalState = UPDATE_ASSOCIATION_MODAL_STATES.REMOVE_ASSOCIATION_MODAL_PRIMARY_ONLY_ASSOCIATION_STATE;
      } else {
        modalState = UPDATE_ASSOCIATION_MODAL_STATES.REMOVE_PRIMARY_ASSOCIATION_MODAL_STATE;
      }
    } else {
      if (isCompanyPrimaryAssociation) {
        modalState = UPDATE_ASSOCIATION_MODAL_STATES.REMOVE_REVERSE_PRIMARY_MODAL_STATE;
      } else {
        modalState = UPDATE_ASSOCIATION_MODAL_STATES.REMOVE_SECONDARY_ASSOCIATION_MODAL_STATE;
      }
    }
    sendOpenUpdateAssociationModal({
      currentState: modalState,
      fromObjectId,
      fromObjectTypeId,
      toObjectId: objectId,
      toObjectTypeId: objectTypeId
    });
  }, [isCompanyPrimaryAssociation, totalAssociations, sendOpenUpdateAssociationModal, fromObjectId, fromObjectTypeId, objectId, objectTypeId, isPrimary]);
  const useActions = useBehavior(ChickletHoverActions, objectTypeId, hasScope, isUngated);
  const {
    PrimaryAction,
    SecondaryActions
  } = useActions({
    associatedObjectsIds,
    canEdit: canEditAssociation,
    canView,
    currentState: isPrimary ? PRIMARY_STATE : SECONDARY_STATE,
    fromObjectTypeId,
    handleOpenEditAssociationModal,
    handleOpenPrimaryModal,
    handleOpenRemoveAssociationModal,
    objectId,
    onPreview,
    primaryAssociationObjectId,
    toObjectTypeId: objectTypeId,
    fromObjectId,
    associationDefinition: unlabeledAssociationType,
    isUngated,
    hasScope
  });
  return /*#__PURE__*/_jsxs(ButtonGroup, {
    children: [PrimaryAction && /*#__PURE__*/_jsx(ChickletHoverButton, Object.assign({}, PrimaryAction)), canEditAssociation && SecondaryActions.length > 0 && /*#__PURE__*/_jsx(Dropdown, {
      buttonSize: "xs",
      buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'cardAssociatedObjects.chicklet.ChickletHoverActionsDropdown.dropdownButtonText'
      }),
      "data-selenium-test": "chicklet-more-btn",
      "data-test-id": "chicklet-more-btn",
      placement: "bottom left",
      buttonUse: "tertiary",
      children: /*#__PURE__*/_jsx(List, {
        children: SecondaryActions.map((actionProps, index) => {
          return actionProps ? /*#__PURE__*/_jsx(ListItem, {
            children: /*#__PURE__*/_jsx(ChickletHoverButton, Object.assign({}, actionProps))
          }, index) : undefined;
        })
      })
    })]
  });
};
ChickletAssociationButtons.propTypes = {
  associationDefinitions: PropTypes.array.isRequired,
  associatedObjectsIds: PropTypes.array.isRequired,
  fromObjectTypeId: PropTypes.string.isRequired,
  canView: PropTypes.bool.isRequired,
  objectId: PropTypes.number.isRequired,
  objectTypeId: PropTypes.string.isRequired,
  onPreview: PropTypes.func.isRequired,
  primaryAssociationObjectId: PropTypes.number
};
export default ChickletAssociationButtons;